<template>
    <div>
        <label for="search-team-users" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Add team members</label>
        <div class="relative">
            <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 19l-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                </svg>
            </div>
            <input type="search" id="search-team-users" 
                class="block w-full pl-9 pr-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500" 
                placeholder="Search users by email..." 
                v-model="searchText"
                @keyup.enter="handleSearch">
        </div>

        <!-- Search Results -->
        <div class="mt-4 space-y-2">
            <Loading v-if="loading" class="w-full"/>
            <div v-else>
                <!-- Search Results -->
                <div class="block mb-2 text-sm font-medium text-red-700 dark:text-white text-center" 
                    v-if="showErrorMessage">
                    {{showMessage}}
                </div>
                <div v-for="user in filteredSearchResults" 
                    :key="'search-' + user.id"
                    class="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                    <UserAvatar :user="user" :size="40" />
                    <div class="flex-1 min-w-0">
                        <span class="block text-sm font-semibold text-gray-900 dark:text-white truncate">{{ resultName(user) }}</span>
                        <span v-if="resultEmail(user)" class="block text-xs text-gray-500 dark:text-gray-400 truncate">({{ resultEmail(user) }})</span>
                    </div>
                    <Button @click="handleUserSelected(user)"
                        class="flex-shrink-0 inline-flex items-center px-4 py-2 text-sm font-semibold text-center text-white rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 focus:ring-4 focus:outline-none focus:ring-indigo-300 transition-all">
                        Add
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, defineExpose } from 'vue';
import Loading from '@/components/TriadLoad.vue';
import Button from '@/components/form/TriadButton.vue'
import UserAvatar from '@/components/common/UserAvatar.vue';
import toastStore from "@/store/toast.store";

const props = defineProps({
    exclude: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['user-selected']);
const initialLoad = ref(true)
const searchText = ref("");
const loading = ref(false);
const searchResults = ref([]);

const filteredSearchResults = computed(() => {
    return searchResults.value.filter(user => !props.exclude.includes(user.id))
});

const showErrorMessage = computed(() => {
    return filteredSearchResults.value.length == 0 && !initialLoad.value
})

const showMessage = computed(() => {
    if (showErrorMessage.value) {
        return 'User not found or user already added to team'
    } else {
        return '';
    }
})

const resultName = (user) => {
    const name = (user.name || '').trim();
    if (name) return name;
    return (user.email || '').trim();
};

const resultEmail = (user) => {
    const name = (user.name || '').trim();
    const email = (user.email || '').trim();
    if (!name || !email) return '';
    return name.toLowerCase() === email.toLowerCase() ? '' : email;
};

const handleSearch = async () => {
    if (isEmail(searchText.value)) {
        loading.value = true;
        initialLoad.value = false;
        try {
            const response = await fetch(`/api/user/search?q=${searchText.value}`);
            if (response.status === 200) {
                const data = await response.json();
                searchResults.value = data.filter(user => !props.exclude.includes(user.id));
                if (searchResults.value.length > 0) {
                    toastStore.dispatch('addMessage', {
                        message: searchResults.value.length + ' users found',
                        icon: 'success',
                        ttl: 3000
                    });
                }
            } else {
                throw new Error('Failed to search users');
            }
        } catch (error) {
            console.error('Error searching users:', error);
            toastStore.dispatch('addMessage', {
                message: 'Error searching users',
                icon: 'error',
                ttl: 3000
            });
        } finally {
            loading.value = false;
        }
    } else {
        toastStore.dispatch("addMessage", {
            message: "Please enter a valid email address",
            icon: "error",
            ttl: 3000
        });
    }
};

const isEmail = (text) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);
}

const handleUserSelected = (user) => {
    emit('user-selected', user);
};

const clearSearch = () => {
    searchText.value = '';
    searchResults.value = [];
    initialLoad.value = true;
};

defineExpose({ clearSearch });
</script> 